import { Tooltip } from "antd";

import { deriveUsageBuckets } from "./SegmentedUsageBar";

type ManagePlanUsageBarProps = {
  /** Total credits used this cycle (plan-pool used + top-up used). */
  used: number;
  /** Total credits available this cycle (plan-pool purchased + top-up purchased). */
  total: number;
  /** Top-up credits purchased this cycle. Defaults to 0. */
  topupPurchased?: number;
  /** Top-up credits already consumed this cycle. Defaults to 0. */
  topupUsed?: number;
};

export function ManagePlanUsageBar({
  used,
  total,
  topupPurchased = 0,
  topupUsed = 0,
}: ManagePlanUsageBarProps) {
  const buckets = deriveUsageBuckets({
    used,
    total,
    topupPurchased,
    topupUsed,
  });
  const showBreakdown = buckets.planTotal > 0 && topupPurchased > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-xs leading-4 text-[#6c7689]">
          Current Usage
        </span>
        <span className="h-0 flex-1 border-b border-dashed border-[#e8eaed]" />
        <span className="shrink-0 text-xs leading-4 text-[#1f2633]">
          {Math.round(used).toLocaleString()} /{" "}
          {Math.round(total).toLocaleString()}
          {showBreakdown ? (
            <Tooltip
              title={`${Math.round(buckets.planTotal).toLocaleString()} credits from monthly Org Credit Pack, and ${Math.round(topupPurchased).toLocaleString()} from refilled credits`}
            >
              <span
                data-test="usage-bar-breakdown"
                aria-label="Credits breakdown"
                className="ml-1 inline-flex h-3.5 w-3.5 cursor-help items-center justify-center align-middle text-[#a4aab7] hover:text-[#6c7689]"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="8" cy="8" r="6.4" />
                  <path d="M8 7.2v3.6" />
                  <circle
                    cx="8"
                    cy="5.4"
                    r="0.6"
                    fill="currentColor"
                    stroke="none"
                  />
                </svg>
              </span>
            </Tooltip>
          ) : null}
        </span>
      </div>
      {/* Single-color bar — matches the /spending page tile. The (N + M)
          breakdown next to the count + tooltip already explain composition,
          so we don't need the segmented coloring here. */}
      <div className="h-[5px] w-full overflow-hidden rounded-[32px] bg-[#f3f4f6]">
        <div
          className="h-[5px] rounded-[32px] bg-[#0099ff] transition-all"
          style={{
            width: `${total > 0 ? Math.min(100, (used / total) * 100) : 0}%`,
          }}
        />
      </div>
    </div>
  );
}
